import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, CircularProgress, FormControl, Stack, TextField, Typography } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowRightAlt';
import { Link } from 'react-router-dom';
import useToast from '../hooks/useToast';
import Logo from '../components/Logo';
import String from '../components/String';
import Layout from '../components/Layout';
import SetupStep from '../components/SetupStep';
import { useQueryParams } from '../hooks/useQueryParams';
import { useSession } from '../contexts/session';
import { redeemExpCode, supabase } from '../utils/supabase';
import { HOSTNAME } from '../../config/config';
import { addDays, daysFromNow } from '../utils/utils';
import { useLanguage } from '../contexts/language';

const RedeemExpAnonymous = () => {
  const toast = useToast();
  const queryParams = useQueryParams();
  const codeFromUrl = queryParams.get('code') ?? '';
  const decodedCodeFromUrl = decodeURIComponent(codeFromUrl);
  const expName = queryParams.get('expName');

  const decodedExpName = expName ? decodeURIComponent(expName) : '';

  const { user } = useSession();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(decodedCodeFromUrl);
  const [redeemLoading, setRedeemLoading] = useState(false);

  const [result, setResult] = useState(null);
  const performAnonymousRedeem = !user?.email;

  const { content } = useLanguage();

  const redeem = useCallback(async () => {
    if (!email || !code) return;

    setRedeemLoading(true);

    const { data, error } = await redeemExpCode({ email, code, redirectTo: HOSTNAME });
    if (error) {
      toast.error(error.toString());
      console.error(error);
    } else if (data) {
      setResult(data);
      toast.success(content('experience_redeemed_successfully'));
    } else {
      toast.error(content('something_went_wrong'));
      console.error('Something went wrong. Missing response.');
    }

    setRedeemLoading(false);
  }, [email, code, content]);

  const retrieveAnonymousUserId = useCallback(async () => {
    // if not logged, perform anonymous login
    if (!user?.id) {
      const { data: anonymousUser, error: anonymousUserError } = await supabase.auth.signInAnonymously();

      if (anonymousUserError) {
        toast.error(content('something_went_wrong'));
        console.error(anonymousUserError);
        return;
      }
      return anonymousUser.user.id;
    }
    // if the anonymous user already has a valid session, use that
    return user.id;
  }, [user, content]);

  const redeemAnonymously = useCallback(async () => {
    try {
      if (!code) return;
      setRedeemLoading(true);

      const anonymousAccessUserId = await retrieveAnonymousUserId();
      if (!anonymousAccessUserId) return;

      const { data, error } = await redeemExpCode({
        code,
        redirectTo: HOSTNAME,
        anonymousAccess: true,
        anonymousAccessUserId,
      });

      if (error) {
        toast.error(error.toString());
        console.error(error);
      } else if (data) {
        setResult(data);
        toast.success(content('experience_redeemed_successfully'));
      } else {
        toast.error(content('something_went_wrong'));
        console.error('Something went wrong. Missing response.');
      }

      setRedeemLoading(false);
    } catch (error) {
      toast.error(content('something_went_wrong'));
      console.error(error);
    }
  }, [retrieveAnonymousUserId, code, content]);

  const handleKeyUp = (e) => {
    if (e.key === 'Enter') redeem();
  };

  useEffect(() => {
    if (user?.email) {
      setEmail(user?.email);
    }
  }, [user?.email]);

  // checks expiration date. If number of days greater than 31, use date instead
  const redeemedUntil =
    result?.userExperience?.created_at &&
    result?.accessCode?.access_expires_after &&
    daysFromNow(addDays(result.userExperience.created_at, result.accessCode?.access_expires_after)) > 0;

  const redeemedUntilDays = redeemedUntil
    ? daysFromNow(addDays(result.userExperience.created_at, result.accessCode?.access_expires_after))
    : null;

  const redeemedUntilDate = redeemedUntil
    ? addDays(result.userExperience.created_at, result.accessCode?.access_expires_after).toDateString()
    : null;

  const goToExperienceUrl = React.useMemo(() => {
    const expId = result?.userExperience?.experience_id;
    return expId ? `/setup/1/?expId=${expId}` : '/';
  }, [result?.userExperience?.experience_id]);

  return (
    <Layout
      fullscreen
      framed
      top={
        <Box mt="8vh" textAlign="center">
          <Logo />
        </Box>
      }
    >
      <SetupStep
        themeSwitcher
        form={
          !result ? (
            <Stack spacing={3} mb={2} width="100%">
              <Typography variant="title" fontWeight="bold" mb={2}>
                <String s="redeem_exp_title" /> <em>{decodedExpName}</em>
              </Typography>
              <FormControl variant="outlined" fullWidth>
                <TextField
                  value={code || ''}
                  onChange={(e) => setCode(e.currentTarget.value)}
                  type="text"
                  label="Code"
                  required
                  onKeyUp={handleKeyUp}
                  disabled={redeemLoading}
                  inputProps={{ 'aria-label': content('aria_experience_redeem_code_input') }}
                  data-testid="redeem-exp-anonym-code-input"
                />
              </FormControl>

              {performAnonymousRedeem ? (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={redeemAnonymously}
                  disabled={!code || redeemLoading}
                  fullWidth
                  aria-label={content('aria_redeem_experience')}
                  endIcon={
                    redeemLoading ? (
                      <CircularProgress
                        color="secondary"
                        style={{ marginLeft: 8 }}
                        size="20px"
                        role="status"
                        aria-label={content('aria_loading')}
                      />
                    ) : null
                  }
                  data-testid="redeem-exp-anonym-confirm-btn"
                >
                  <String s="redeem_exp_button" />
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={redeem}
                  disabled={!email || !code || redeemLoading}
                  fullWidth
                  aria-label={content('aria_redeem_experience')}
                  endIcon={
                    redeemLoading ? (
                      <CircularProgress
                        color="secondary"
                        style={{ marginLeft: 8 }}
                        size="20px"
                        role="status"
                        aria-label={content('aria_loading')}
                      />
                    ) : null
                  }
                >
                  <String s="redeem_exp_button" />
                </Button>
              )}

              <Button
                color="secondary"
                variant="text"
                startIcon={<BackIcon style={{ transform: 'rotate(-180deg)' }} />}
                component={Link}
                to="/"
                aria-label={content('go_home')}
              >
                <String s="go_home" />
              </Button>
            </Stack>
          ) : (
            <Stack spacing={6} mb={2} width="100%">
              <Typography variant="title" fontWeight="bold" mb={2}>
                <String s="redeem_exp_result_title" /> <em>{decodedExpName}</em>
              </Typography>
              <Stack spacing={0} width="100%">
                {result.alreadyRedeemed ? (
                  <Typography>
                    <String s="alreadly_redeemed" />
                  </Typography>
                ) : (
                  <Typography
                    data-testid="redeem-exp-anonym-success-message"
                    role="status"
                    aria-live="polite"
                  >
                    <String s="redeem_ok" />
                  </Typography>
                )}

                {redeemedUntil && redeemedUntilDays <= 31 && (
                  <Typography>
                    <String s="redeemed_until_days" /> <strong>{redeemedUntilDays} days.</strong>
                  </Typography>
                )}

                {redeemedUntil && redeemedUntilDays > 31 && (
                  <Typography>
                    <String s="redeemed_until_date" /> <strong>{redeemedUntilDate}</strong>.
                  </Typography>
                )}
              </Stack>

              <Button
                href={goToExperienceUrl}
                color="primary"
                variant="contained"
                fullWidth
                aria-label={content('aria_go_to_experience')}
                data-testid="redeem-exp-anonym-gotoexp-btn"
              >
                <String s="go_to_exp_button" />
              </Button>
            </Stack>
          )
        }
      />
    </Layout>
  );
};

export default RedeemExpAnonymous;
